/**
 * URLs that go to analytics lose their secrets and personal details first.
 *
 * Emailed and returning links carry secrets: the wallet link `t` (`/deal-wallet#t=…`, older
 * `?t=…`), `token` (password reset, email verification, email preferences, business invitation
 * acceptance), `email` (the emailed "Set a password" links: `/forgot-password?email=…`),
 * `session_id` (Stripe's return `?session_id=…`, the Google / Emergent sign-in return
 * `#session_id=…`) and `invite` (the business invitation sign-up link `/signup?invite=…`).
 *
 * The first line of defence runs before any tag loads: the wallet guard and the URL secret
 * guard in public/index.html take these values out of the address bar and keep them for the
 * page in sessionStorage (lib/urlSecrets.js). This file is the second line, for every URL that
 * is reported to analytics anyway (App.js GTMPageTracker; the gtag config in index.html applies
 * the same rule inline): redactUrl drops the named params from the query and never sends a
 * fragment at all (a fragment is page state, and it is where sign-in returns put their ids).
 */

/** Parameter names removed from the query (compared case-insensitively, any encoding). */
export const REDACTED_PARAMS = ['t', 'token', 'email', 'session_id', 'invite'];

/** The decoded name of one `name=value` part (`+` is a space, as in URLSearchParams). */
function paramName(part) {
  const eq = part.indexOf('=');
  const raw = eq < 0 ? part : part.slice(0, eq);
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' ')).toLowerCase();
  } catch {
    return raw.toLowerCase();
  }
}

/** `a=1&t=2` without the named params; the other parts are kept exactly as written. */
function stripParams(str, names) {
  if (!str) return str;
  return str
    .split('&')
    .filter((part) => !names.includes(paramName(part)))
    .join('&');
}

/**
 * `href` without its fragment and without the named params in its query string. The rest is
 * kept as written (no re-encoding); an emptied `?` is dropped. A URL with no fragment and none
 * of the params comes back unchanged (the same string).
 *
 * @param {string} href
 * @param {string[]} [names=REDACTED_PARAMS]
 * @returns {string}
 */
export function redactUrl(href, names = REDACTED_PARAMS) {
  if (typeof href !== 'string' || !href) return href;
  const lower = names.map((n) => String(n).toLowerCase());
  const hashAt = href.indexOf('#');
  const beforeHash = hashAt < 0 ? href : href.slice(0, hashAt);
  const queryAt = beforeHash.indexOf('?');
  const base = queryAt < 0 ? beforeHash : beforeHash.slice(0, queryAt);
  const query = queryAt < 0 ? null : beforeHash.slice(queryAt + 1);
  const q = query === null ? null : stripParams(query, lower);
  if (hashAt < 0 && q === query) return href;
  return `${base}${q ? `?${q}` : ''}`;
}
